import { useEffect, useState } from "react";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "./ProductCard";
import { Loader2, PackageX, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

interface ProductGridProps {
  limit?: number;
  query?: string;
  products?: ShopifyProduct[];
}

export const ProductGrid = ({ 
  limit = 20, 
  query, 
  products: providedProducts
}: ProductGridProps) => {
  const [products, setProducts] = useState<ShopifyProduct[]>(providedProducts || []);
  const [isLoading, setIsLoading] = useState(!providedProducts);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If products are provided directly, don't fetch
    if (providedProducts) {
      setProducts(providedProducts);
      setIsLoading(false);
      return;
    }

    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const data = await fetchProducts(limit, query);
        setProducts(data);
        
        setError(null);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Unable to load products. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [limit, query, providedProducts]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-16"
      >
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-display text-xl font-light mb-2">Connection Issue</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          {error}
        </p>
      </motion.div>
    );
  }

  if (products.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-20"
      >
        <PackageX className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-display text-2xl font-light mb-2">No Products Available</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Our collection is coming soon. Check back shortly to discover our exclusive creations.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
      {products.map((product, index) => (
        <ProductCard 
          key={product.node.id} 
          product={product} 
          index={index}
        />
      ))}
    </div>
  );
};
